import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Modal, Table } from 'react-bootstrap';
import axios from '../../../../api/axios';
import { GlobalContext } from '../../../../store/GlobalContext';

type Announcement = {
  _id: string;
  title: string;
  message: string;
  audience: 'all' | 'customer' | 'admin';
  priority: number;
  isActive: boolean;
  startsAt?: string;
  endsAt?: string;
};

const defaultForm = {
  title: '',
  message: '',
  audience: 'all',
  priority: '0',
  startsAt: '',
  endsAt: '',
  isActive: true,
};

const Announcements = () => {
  const { state } = useContext(GlobalContext);
  const token = state?.auth?.token;

  const [rows, setRows] = useState<Announcement[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [form, setForm] = useState(defaultForm);

  const fetchRows = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get('/announcements', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(response.data?.announcements)
        ? response.data.announcements
        : Array.isArray(response.data)
          ? response.data
          : [];
      setRows(list);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load announcements.');
      setRows([]);
    }
  }, [token]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openCreate = () => {
    setEditing(null);
    setForm(defaultForm);
    setError('');
    setSuccess('');
    setShowDialog(true);
  };

  const openEdit = (row: Announcement) => {
    setEditing(row);
    setForm({
      title: row.title,
      message: row.message,
      audience: row.audience,
      priority: String(row.priority ?? 0),
      startsAt: row.startsAt ? String(row.startsAt).slice(0, 16) : '',
      endsAt: row.endsAt ? String(row.endsAt).slice(0, 16) : '',
      isActive: row.isActive,
    });
    setShowDialog(true);
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token) return;

    const payload = {
      title: form.title.trim(),
      message: form.message.trim(),
      audience: form.audience as 'all' | 'customer' | 'admin',
      priority: Number(form.priority || 0),
      startsAt: form.startsAt || undefined,
      endsAt: form.endsAt || undefined,
      isActive: form.isActive,
    };

    try {
      if (editing?._id) {
        await axios.put(`/announcements/${editing._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('Announcement updated successfully.');
      } else {
        await axios.post('/announcements', payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('Announcement created successfully.');
      }

      setShowDialog(false);
      await fetchRows();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save announcement.');
    }
  };

  const handleDelete = async (row: Announcement) => {
    if (!token) return;
    if (!window.confirm(`Delete announcement "${row.title}"?`)) return;

    try {
      await axios.delete(`/announcements/${row._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Announcement deleted successfully.');
      await fetchRows();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete announcement.');
    }
  };

  return (
    <>
      <Card className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Announcements</h4>
          <Button variant="dark" onClick={openCreate}>Add Announcement</Button>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Title</th>
              <th>Audience</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Window</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center">No announcements found.</td>
              </tr>
            ) : rows.map((row) => (
              <tr key={row._id}>
                <td>
                  <div><strong>{row.title}</strong></div>
                  <small>{row.message}</small>
                </td>
                <td>{row.audience}</td>
                <td>{row.priority}</td>
                <td>{row.isActive ? 'Active' : 'Inactive'}</td>
                <td>
                  <small>
                    {row.startsAt ? new Date(row.startsAt).toLocaleString() : 'Now'}
                    {' - '}
                    {row.endsAt ? new Date(row.endsAt).toLocaleString() : 'Open'}
                  </small>
                </td>
                <td>
                  <Button size="sm" variant="warning" className="me-2" onClick={() => openEdit(row)}>Edit</Button>
                  <Button size="sm" variant="danger" onClick={() => handleDelete(row)}>Delete</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <Modal show={showDialog} onHide={() => setShowDialog(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Edit Announcement' : 'Add Announcement'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSave}>
            <Form.Group className="mb-2">
              <Form.Label>Title</Form.Label>
              <Form.Control value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Message</Form.Label>
              <Form.Control as="textarea" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required />
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-2">
                  <Form.Label>Audience</Form.Label>
                  <Form.Select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}>
                    <option value="all">All</option>
                    <option value="customer">Customer</option>
                    <option value="admin">Admin</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-2">
                  <Form.Label>Priority</Form.Label>
                  <Form.Control type="number" min="0" max="10" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-2">
                  <Form.Label>Starts At (optional)</Form.Label>
                  <Form.Control type="datetime-local" value={form.startsAt} onChange={(e) => setForm({ ...form, startsAt: e.target.value })} />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-2">
                  <Form.Label>Ends At (optional)</Form.Label>
                  <Form.Control type="datetime-local" value={form.endsAt} onChange={(e) => setForm({ ...form, endsAt: e.target.value })} />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Active"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" variant="dark">{editing ? 'Update' : 'Create'}</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Announcements;
